import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCloudUpload } from "react-icons/md";

import RadioButton from "components/__reusable/RadioButton";
import ClassMultipleValueDropdownInput from "components/__reusable/ClassMultipleValueDropdownInput";
import PictureUploadInput from "./PictureUploadInput";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import { PictureType } from "config/constants";
import useAppDispatch from "hooks/useAppDispatch";
import { addToUploadQueue } from "features/picture/pictureSlice";
import type { UploadPicture } from "types/Pictures";

const PICTURE_TYPE_OPTIONS = [
  { value: PictureType.ai, label: "_upload._individual_picture" },
  { value: PictureType.class, label: "_upload._entire_class_picture" },
];

const STEP_BADGE_CLASS_NAME =
  "flex h-8 min-w-[2.5rem] items-center justify-center rounded-md bg-[#9B9B9B] px-3 text-base font-semibold leading-none text-white";

const Form = () => {
  const { t } = useTranslation();
  const [pictureType, setPictureType] = useState<PictureType>(1);
  const [classId, setClassId] = useState<string[]>([]);
  const [pictures, setPictures] = useState<UploadPicture[]>([]);

  const dispatch = useAppDispatch();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    dispatch(
      addToUploadQueue({ uploadPictures: pictures, classId, pictureType })
    );
    setPictureType(1);
    setClassId([]);
    setPictures([]);
  };

  const renderStepBadge = (stepNumber: number) => (
    <div className={STEP_BADGE_CLASS_NAME}>{stepNumber}</div>
  );

  return (
    <form
      className="h-full flex flex-col space-y-3 lg:space-y-5"
      onSubmit={submit}
    >
      <div className="lg:flex lg:items-center space-y-1 lg:space-y-0">
        <div className="lg:w-1/4 text-xl lg:text-2xl font-semibold flex items-center space-x-2">
          {renderStepBadge(1)}
          <p>{t("_upload._classification_select")}</p>
        </div>
        <div className="lg:w-3/4 flex space-x-5">
          {PICTURE_TYPE_OPTIONS.map((typeOption) => (
            <div key={typeOption.value} className="space-x-2">
              <RadioButton
                name={"picture_type"}
                label={t(typeOption.label)}
                value={typeOption.value.toString()}
                isChecked={typeOption.value === pictureType}
                disabled={false}
                onChange={(value) => {
                  setPictureType(parseInt(value));
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="lg:flex lg:items-center space-y-1 lg:space-y-0">
        <div className="lg:w-1/4 text-xl lg:text-2xl font-semibold flex items-center space-x-2">
          {renderStepBadge(2)}
          <p>{t("_upload._class_select")}</p>
        </div>
        <div className="lg:w-3/4">
          <ClassMultipleValueDropdownInput
            classId={classId}
            onChange={(classId) => setClassId(classId)}
          />
        </div>
      </div>
      <div className="flex-1 flex lg:items-start flex-col lg:flex-row space-y-1 lg:space-y-0">
        <div className="lg:w-1/4 text-xl lg:text-2xl font-semibold flex items-center space-x-2">
          {renderStepBadge(3)}
          <p>{t("_upload._picture_select")}</p>
        </div>
        <div className="lg:w-3/4 h-full ">
          <PictureUploadInput
            pictures={pictures}
            onUpload={(pictures) => setPictures([...pictures])}
          />
        </div>
      </div>
      <div className="lg:flex lg:items-center lg:justify-end lg:space-x-3 space-y-1 lg:space-y-0">
        <div className="text-xl lg:text-2xl font-semibold flex items-center space-x-2">
          {renderStepBadge(4)}
        </div>
        <div className="w-1/3">
          <PrimaryActionButton
            isButton={false}
            label={
              <div className="w-full flex justify-center items-center space-x-3">
                <MdCloudUpload className="text-2xl" />
                <p>{t("_upload._upload")}</p>
              </div>
            }
            disabled={classId.length === 0 || pictures.length === 0}
          />
        </div>
      </div>
    </form>
  );
};

export default Form;
